import { GATE_DEFS, type GateId } from "./qa";

/**
 * THE EXPORT GATE SPECIFICATION — data only, no JSX.
 *
 * /pipeline is REFERENCE. It answers three questions and nothing else: what must be true
 * before ATLAS will export, what exactly each check checks, and what you get when it passes.
 * It shows NO run's state. That is why there is not a single verdict, status or count of a
 * live thing in this file — and why the screen that consumes it renders zero StatusPills.
 *
 * WHAT THIS FILE REPLACES. The old screen mapped the COMPILER'S BUILD STATE onto a RUN
 * verdict pill:
 *
 *     status === "installed" || status === "web live" ? "approved" : "needs_review"
 *
 * so "Phase 1 · ATLAS package onboarding" rendered a green APPROVED pill whose own title
 * attribute — straight from STATUS_META.approved — read "A human recorded an approval for
 * this run." No human approved a build phase. That is the exact class of bug F3 made
 * uncompilable on /review (a tick with no evidence), and it was still live here, nine times
 * over. It also destroyed its own data: seven distinct source words (installed, live gated,
 * draft path, QA live, deployed, web live, tests started) collapsed into two pills, so five
 * of the nine phases rendered identically. The words survive here as prose. The pills do not
 * survive at all.
 *
 * THE GATE -> VALIDATOR MAPPING IS A BACKEND FACT, NOT A DESIGN INVENTION.
 * Source of truth: backend/cloudformation.yaml, buildQaLevelReport(). Verified against it:
 *
 *     validatorQaLevel("protected-facts",   [V0,V1,V2,V3,V4,V5,V6,V7,V8,V8b,V9])
 *     validatorQaLevel("editable-blocks",   [V10,V11,V12,V13])
 *     validatorQaLevel("research-rigor",    [V18])
 *     validatorQaLevel("patch-application", [V19])
 *     validatorQaLevel("rendering",         [V14,V14a,V15,V16,V17], layoutReport?.pass === true)
 *     reviewerQaLevel(reviewScores)                                   <- no validator at all
 *
 * Twenty-two validators. The old panel meta said "V1-V19 installed", which undercounts by
 * three: V0 (the fail-safe register gate), V8b and V14a are extra ids, not sub-cases. A precision
 * instrument that cannot count its own checks has no business telling anyone their document is safe.
 *
 * IF THE BACKEND MOVES A VALIDATOR BETWEEN LEVELS, THIS PAGE SILENTLY LIES. That is why the
 * mapping is asserted by a contract test (tests/frontend-contract.test.ts) against the six frozen
 * level ids and the validator count, rather than merely rendered — duplication is the point.
 * Without that assertion this fix has a half-life.
 *
 * Every validator description below is derived from validators/validators.ts source, not
 * written from imagination. Two that are easy to get wrong, and were checked in the source:
 *   - V10 "budgets" is `patch.charCountAfter > block.maxCharacters` and `lineCountAfter >
 *     block.maxLines`. It is a LENGTH budget. Read as a money budget it is meaningless, so
 *     the description says so explicitly.
 *   - V8b emits warn(), not hard(). The backend's `aligned` NO LONGER requires warningCount === 0
 *     (only hardFailureCount === 0 and no level in `fail`), so a warning is reported and does NOT
 *     block export. That is what `warn` was always supposed to mean; while warnings blocked, the
 *     severity was a lie and a safe edit — one that made a claim MORE tentative — stopped a run
 *     exactly as hard as a protected fact going missing.
 */

export type ValidatorSpec = {
  /** The validator id exactly as the backend reports it. Rendered in mono. */
  id: string;
  description: string;
};

export type GateSpec = {
  id: GateId;
  label: string;
  /** One sentence: what this gate checks. */
  sentence: string;
  /** The validators the backend runs for this level. `reviewers` legitimately has none. */
  validators: ValidatorSpec[];
  /** What the summary line says about the validator count. Reviewer sign-off has no validator. */
  countLabel: string;
};

const PROTECTED_FACTS: ValidatorSpec[] = [
  {
    // V0 IS THE FIRST AND MOST LOAD-BEARING GATE, and this catalogue did not list it.
    //
    // The backend emits it into the `protected-facts` level, and gate STATUS renders from
    // run.qaLoop.levels[] — so a V0 failure has always surfaced correctly. What was missing was its
    // DESCRIPTION, on the page that tells the operator what the compiler checks. That matters more here
    // than anywhere else: V0 is what blocks every run when the registers cannot be loaded or the
    // canonical source's sha does not match the one the run pinned, and an operator staring at a blocked
    // run needs this page to tell them what "V0" means. See the header note: if this list drifts from
    // the backend, this page silently lies.
    id: "V0",
    description:
      "The safety registers loaded, sha-verified, and pinned to this run — and the canonical source is byte-for-byte the document those registers describe. Every block budget is MEASURED, every block selector resolves to exactly one node, and every protected fact inside an editable block carries its <span data-protected> anchor. If any of that cannot be proven, the run BLOCKS: an unloadable register does not mean there are no protected facts, it means we cannot prove any of them survived — and 'cannot prove' is a failure, never a pass."
  },
  {
    id: "V1",
    description:
      "Every protected fact is still present in the final document and its span survived structurally."
  },
  {
    id: "V2",
    description:
      "The set of numbers and currencies in the final document equals the canonical source exactly."
  },
  { id: "V3", description: "The set of dates and date-like tokens equals the canonical source exactly." },
  { id: "V4", description: "Every protected entity name is still present in the final text." },
  {
    id: "V5",
    description:
      "Every citation marker survives and the references section is byte-identical to the baseline."
  },
  { id: "V6", description: "No URL changed during personalization." },
  { id: "V7", description: "Every required term is still present in the final text." },
  {
    id: "V8",
    description:
      "No edit strengthens a claim — no weak-to-strong wording swap, and no patch declaring a strengthened certainty."
  },
  {
    id: "V8b",
    description:
      "Flags an edit that weakens a claim from strong wording to weak — for example \"will\" softened to \"would\". This is a WARNING, not a failure: weakening a claim is not a safety breach (the compiler is forbidden from STRENGTHENING one), so it is reported for your attention and does not block export."
  },
  { id: "V9", description: "No prohibited term or claim appears in the final text." }
];

const EDITABLE_BLOCKS: ValidatorSpec[] = [
  {
    id: "V10",
    description:
      "Every patch targets a known block and stays inside that block's character and line budget. (Budget means length, not money.)"
  },
  {
    id: "V11",
    description:
      "HTML structure outside the allowed text nodes is unchanged, no patch targets an unauthorized block, and no substantive block is edited unless substantive edits were allowed."
  },
  {
    id: "V12",
    description:
      "CSS and asset hashes are unchanged, class attributes are unchanged, and the final document contains no .fit or .fit2 fitting class. Text is never made to fit by changing the layout."
  },
  { id: "V13", description: "Every patch validates against the patch schema." }
];

const RESEARCH_RIGOR: ValidatorSpec[] = [
  {
    id: "V18",
    description:
      "The evidence ledger meets the source-count, distinct-domain and source-tier minimums for the run mode. Every finding has a valid source URL, was accessed within seven days of the run, carries a confidence its source tier supports, and states why it is relevant."
  }
];

const PATCH_APPLICATION: ValidatorSpec[] = [
  {
    id: "V19",
    description:
      "The proposed text of every approved patch is present in the final document. Rejected patches are skipped."
  }
];

const RENDERING: ValidatorSpec[] = [
  {
    id: "V14",
    description: "The rendered page count equals the expected count and page dimensions are unchanged."
  },
  { id: "V14a", description: "No page exceeds its height budget and no block overflows its page." },
  {
    id: "V15",
    description:
      "The masked visual diff and the geometry comparison against the baseline both stay inside tolerance."
  },
  {
    id: "V16",
    description:
      "All fonts load, the render makes zero network fetches, no asset is missing, and no anchor is broken."
  },
  {
    id: "V17",
    description: "The document has no horizontal scroll and no overlapping content at a 375px viewport."
  }
];

const GATE_SENTENCES: Record<GateId, string> = {
  "protected-facts":
    "The facts the compiler may never touch survive personalization unchanged, and no edit strengthens a claim.",
  "editable-blocks":
    "Edits stayed inside the blocks and the budgets the template allows, and touched nothing else.",
  "research-rigor":
    "Every research finding is sourced, dated, recent enough, and no more confident than its source supports.",
  "patch-application": "Every edit you approved actually landed in the final document.",
  rendering:
    "The personalized document renders to the same geometry as the approved baseline, with no layout drift. This check also requires the layout report itself to pass — if it does not, the check fails even when all five validators pass.",
  reviewers:
    "This check has no validator. It reads the final judge verdict and each reviewer's verdict recorded for the run, and it passes only when the final judge returns a pass and no reviewer dissents. The app cannot clear this one for you."
};

const GATE_VALIDATORS: Record<GateId, ValidatorSpec[]> = {
  "protected-facts": PROTECTED_FACTS,
  "editable-blocks": EDITABLE_BLOCKS,
  "research-rigor": RESEARCH_RIGOR,
  "patch-application": PATCH_APPLICATION,
  rendering: RENDERING,
  // Reviewer sign-off runs NO validator. It reads finalJudge + reviewer verdicts from
  // review-scores.json. An empty list here is the honest answer, and the screen says so in
  // words rather than rendering an empty table.
  reviewers: []
};

/**
 * The six gates, in GATE_DEFS order — which IS the backend's qaLoopPolicy.requiredLevelIds
 * order, and therefore the same six, in the same order, that the operator meets on /review.
 * Deriving from GATE_DEFS rather than re-listing them is what stops the spec page and the
 * live QA instrument drifting apart.
 */
export const GATE_SPECS: GateSpec[] = GATE_DEFS.map(({ id, label }) => {
  const validators = GATE_VALIDATORS[id];
  return {
    id,
    label,
    sentence: GATE_SENTENCES[id],
    validators,
    // "1 validators" is the kind of small dishonesty-adjacent sloppiness that tells a serious
    // professional the tool was not proof-read. Research rigor and Patch application each run
    // exactly one. The design copy specifies the singular; the code now produces it.
    countLabel: validators.length
      ? `${validators.length} ${validators.length === 1 ? "validator" : "validators"}`
      : "Human verdict — no validator"
  };
});

/** 22 (V0 + V1-V19 + V8b + V14a). Asserted by tests/frontend-contract.test.ts. */
export const VALIDATOR_COUNT = GATE_SPECS.reduce((total, gate) => total + gate.validators.length, 0);

export const GATE_COUNT = GATE_SPECS.length;

/**
 * The three groups are PRESENTATIONAL — they let /pipeline show the document the product
 * exists to produce as a document, and file the other ten under two quiet sub-labels. They
 * add no item, remove no item and change no count: ARTIFACT_SPECS stays the single source
 * and ARTIFACT_COUNT stays 12, derived from it.
 */
export type ArtifactGroup = "document" | "evidence" | "provenance";

export type ArtifactSpec = { id: string; description: string; group: ArtifactGroup };

/**
 * The run package. Twelve items, produced when a run is packaged.
 *
 * The typography here was precisely INVERTED before: the descriptions were set in UPPERCASE
 * MONO with caps tracking ("DETERMINISTIC FACT COMPARISON", "BEFORE AND AFTER PAGE IMAGES")
 * while the real identifiers — the filenames — got sans 13px semibold. The filename IS the
 * identifier and belongs in mono; the description is prose and belongs in sentence case.
 */
export const ARTIFACT_SPECS: ArtifactSpec[] = [
  { id: "final.html", description: "The personalized document.", group: "document" },
  { id: "final.pdf", description: "The personalized document, rendered.", group: "document" },
  {
    id: "change-ledger.html",
    description: "Every edit, before and after, on one readable page.",
    group: "evidence"
  },
  {
    id: "research-brief.md",
    description: "The research synthesis behind the proposed edits.",
    group: "evidence"
  },
  {
    id: "evidence-ledger.json",
    description: "Each finding with its source, tier, confidence and access date.",
    group: "evidence"
  },
  {
    id: "protected-facts-report.json",
    description: "The fact-by-fact comparison against the canonical source.",
    group: "evidence"
  },
  { id: "layout-report.html", description: "Page geometry and visual checks.", group: "evidence" },
  {
    id: "review-scores.json",
    description: "Reviewer verdicts and the final judge verdict.",
    group: "evidence"
  },
  {
    id: "run-manifest.json",
    description: "What was run, from which source, and when.",
    group: "provenance"
  },
  {
    id: "fonts/",
    description: "The Meridian fonts, packaged so the document renders without a network.",
    group: "provenance"
  },
  {
    id: "audience-personalization-memo.pdf",
    description: "The guidance memo generated for this audience.",
    group: "provenance"
  },
  { id: "pages/", description: "Before and after images of every page.", group: "provenance" }
];

export const ARTIFACT_COUNT = ARTIFACT_SPECS.length;

/** Derived, never re-listed. The sum of the three groups is ARTIFACT_COUNT, by construction. */
export const artifactsInGroup = (group: ArtifactGroup): ArtifactSpec[] =>
  ARTIFACT_SPECS.filter((artifact) => artifact.group === group);

export type PhaseSpec = { id: string; description: string };

/**
 * The compiler's own build phases — DEMOTED, not deleted.
 *
 * FLAGGED FOR THE OWNER: this content describes the compiler's build, not the operator's
 * run, and arguably does not belong in an operator tool at all. Demoting rather than
 * deleting, because deleting product content is an owner's call, not an implementer's. It is
 * one line to remove from PipelinePage if he agrees.
 *
 * The nine false verdicts are gone. Each phase's REAL state word survives as prose — the
 * seven distinct words the old ternary annihilated.
 */
export const PHASE_SPECS: PhaseSpec[] = [
  { id: "Phase 0", description: "Environment and source audit. Installed." },
  { id: "Phase 1", description: "ATLAS package onboarding. Installed." },
  { id: "Phase 2", description: "Deterministic core. Installed." },
  { id: "Phase 3", description: "Research subsystem. Live, gated." },
  { id: "Phase 4", description: "Controlled editing. Draft path." },
  { id: "Phase 5", description: "Review and repair loop. QA live." },
  { id: "Phase 6", description: "Claude design runtime. Deployed." },
  { id: "Phase 7", description: "Web and chat service. Web live." },
  { id: "Phase 8", description: "Evaluation hardening. Tests started." }
];

/** The two non-negotiables. Static rules — they go in a RuleList, never a DataList. */
export const EXPORT_RULES = [
  {
    title: "Every check must pass",
    body: "A check that returned no verdict counts as a FAILURE — 'we could not prove it' is never a pass. A warning is different: it is a real finding that is not a safety breach, so it is shown to you and does not block the export. Only hard failures block."
  },
  {
    title: "A human verdict is required",
    body: "Reviewer sign-off reads a final judge verdict recorded for the run. No machine check can supply it, and the other five passing does not waive it."
  }
];

/** True when a URL hash names one of the six gates, so /pipeline#rendering can deep-link. */
export function gateIdFromHash(hash: string): GateId | null {
  const id = hash.replace(/^#/, "");
  const match = GATE_SPECS.find((gate) => gate.id === id);
  return match ? match.id : null;
}
